import lodashGet from "lodash/get";
import lodashForOwn from "lodash/forOwn";
import lodashHas from "lodash/has";
import camelCase from "lodash/camelCase";
import lodashMatchesProperty from "lodash/matchesProperty";
import lodashFilter from "lodash/filter";
import { formatFileSize } from "@/common/basic.utilities";
import { getMaritimeRiskCategories } from "@/services/controlRisks/risks.service";

import { ENDPOINT_WOD } from "@/common/constants/api";
import {
  removeHtmlTags,
  getUrlDomain,
  getReferences,
  upperCaseFirstLetter,
  parseStringToObject,
  addTargetToLinks,
} from "@/common/basic.utilities";
import { encodeAllURLParameters } from "@/common/api.utilities";
import { httpGet } from "@/services/api.service";
import { getCountryByCode, getWaterByCode } from "@/common/maps/utilities";
import {
  findTopic,
  findTopicAllFlatten,
  getTopicKey,
  findInfluence,
  formatCategoriesForV2,
  getTopicsStringFromTopicsScores,
} from "@/services/events/topics.service";

import { isFeatureUnlocked } from "@/common/featureLock.js";
import { getFormatted } from "@/common/date.utilities";
import { getLanguage } from "@/services/common/translator.service";
import { hasMaritimeAccess } from "@/services/user/user.service";

//TODO get this file from a dependency repo
const MARITIME_MAPPING = require("@/mappings/content_maritime_incident_mapping.json");
const SECURITY_MAPPING = require("@/mappings/content_security_incident_mapping.json");

export { _getArticles as getArticles };
export { _getFilterCounts as getFilterCounts };
export { _formatFeatures as formatFeatures };
export { _formatFeature as formatFeature };
export { _dataToCopy as dataToCopy };
export { _getCountriesDisplayMetadata as getCountriesDisplayMetadata };

const _getArticles = async (params, requestTimestamp) => {
  let articles;

  try {
    let url;
    let idsPulled = params.idsPulled ? params.idsPulled : [];

    // AVAILABLE PARAMS TO USE
    // aoiId, bounds, clusterId, end, fields, pageOffset, pageSize,
    // precision, search, sortDirection, sources, start, topics
    // eventType, verifiedType, riskCategories, severityMin, severityMax, tags

    await removeUnauthorizedParams(params);

    if (params.type || params.topics) {
      let keys = [];
      let TYPES = params.type ? params.type.split(",") : null;
      if (params.topics) {
        if (
          typeof params.topics === "string" ||
          params.topics instanceof String
        ) {
          TYPES = params.topics.split(",");
        } else if (Array.isArray(params.topics)) {
          TYPES = params.topics;
        }
      }
      if (params.eventType !== "verified" && params.eventType !== "all") {
        TYPES.forEach((item) => {
          if (item) {
            const TOPIC = findTopicAllFlatten(item, true, false);
            if (TOPIC) {
              const field = TOPIC.parentIds ? "apiName" : "type";
              const type = TOPIC[field];
              const key = getTopicKey(type, true);
              keys.push(key);
            } else {
              if (isFeatureUnlocked("influences")) {
                const INFLUENCE = findInfluence(item);
                if (INFLUENCE) {
                  keys.push(item);
                }
              }
            }
          }
        });
        //TODO: Review if there are breaking changes.
        params.topics = keys.join();
      }
    }
    if (params.next) {
      url = params.next;
    } else {
      url = encodeAllURLParameters({
        baseEndpoint: ENDPOINT_WOD,
        params: params,
      });
    }
    const RESULT = await httpGet({ url: url });
    const { data } = JSON.parse(RESULT);

    articles = data ? data.features : [];

    if (params.useNext) {
      if (!params.articles) {
        params.articles = [];
      }
      articles = data ? data.features.concat(params.articles) : [];
    } else {
      articles = data ? data.features : [];
    }

    if (params.useNext && data.metadata && data.metadata.next) {
      params.next = data.metadata.next;
      params.articles = articles;
      await _getArticles(params);
    }

    await _formatFeatures(articles, requestTimestamp);

    const featuresArr = [];

    if (
      data &&
      data.features &&
      Array.isArray(data.features) &&
      data.features.length
    ) {
      const featuresLen = data.features.length;
      for (let i = 0; i < featuresLen; i++) {
        const feature = data.features[i];
        if (
          !params.clusterArticle &&
          lodashHas(feature, "properties.filtered_cluster_size") &&
          feature.properties.filtered_cluster_size > 1
        ) {
          if (!idsPulled.includes(feature.properties.cluster_id)) {
            idsPulled.push(feature.properties.cluster_id);
            featuresArr.push(feature);
          }
        } else {
          if (params.clusterArticle && i === 0) {
            // ignore this first article in the cluster because it's the same as the top one for the cluster
          } else {
            featuresArr.push(feature);
          }
        }
      }
      data.features = featuresArr;
    }
    params.idsPulled = idsPulled;

    return data;
  } catch (error) {
    console.error(error);
    Promise.reject(
      "Issue getting articles in wod.service.js: " + JSON.stringify(error)
    );
  } finally {
    articles = null;
  }
};

const _getFilterCounts = async (params) => {
  try {
    let url;
    params.includeFilterCounts = true;
    params.pageSize = 0;

    await removeUnauthorizedParams(params);

    url = encodeAllURLParameters({
      baseEndpoint: ENDPOINT_WOD,
      params: params,
    });

    const RESULT = await httpGet({ url: url, noCache: true });
    return lodashGet(JSON.parse(RESULT), "data.metadata.filterCounts");
  } catch (error) {
    Promise.reject(
      "Issue getting filter counts in wod.service.js: " + JSON.stringify(error)
    );
  }
};

const _formatFeatures = async (features, requestTimestamp) => {
  const LEN = features.length;
  for (let i = 0; i < LEN; i++) {
    let feature = features[i];
    await _formatFeature(feature, requestTimestamp);
  }
};

// Mapping from wod response to formatted sub-object to be used later
const _formatFeature = async (feature, tstamp) => {
  if (feature.properties) {
    feature.properties.formatted = {};
    feature.properties.formatted.requestTimestamp = tstamp
      ? tstamp
      : new Date().toISOString();
    feature.properties.formatted.dataType = "wod";
    feature.properties.formatted.title = "Oops, can't find a title";
    feature.properties.formatted.summary = "";
    feature.properties.formatted.language = null;
    feature.properties.formatted.adminInfo = "";

    feature.properties.formatted.type = feature.properties.source
      ? feature.properties.source
      : null;
    feature.properties.formatted.esId = feature.properties.id
      ? feature.properties.id
      : null;
    feature.properties.formatted.clusterId = feature.properties.cluster_id
      ? feature.properties.cluster_id
      : null;
    feature.properties.formatted.clusterSize = feature.properties
      .filtered_cluster_size
      ? feature.properties.filtered_cluster_size
      : null;
    feature.properties.formatted.occurred = feature.properties["@timestamp"]
      ? feature.properties["@timestamp"]
      : null;

    feature.properties.formatted.clusterSize = null;
    if (feature.properties.filtered_cluster_size) {
      feature.properties.formatted.clusterSize =
        feature.properties.filtered_cluster_size;
      feature.properties.formatted.adminInfo += `Cluster size: ${feature.properties.filtered_cluster_size}<br>`;
    }

    feature.properties.formatted.location = null;
    if (
      lodashGet(feature, "geometry.coordinates") &&
      Array.isArray(feature.geometry.coordinates) &&
      feature.geometry.coordinates.length === 2 &&
      typeof feature.geometry.coordinates[0] === "number" &&
      typeof feature.geometry.coordinates[1] === "number"
    ) {
      feature.properties.formatted.location = feature.geometry.coordinates;
    }

    if (lodashGet(feature, "properties.link")) {
      feature.properties.formatted.link = feature.properties.link;
      feature.properties.formatted.summary = feature.properties.link;
    }

    feature.properties.formatted.img = null;
    if (lodashGet(feature, "properties.src_image_url")) {
      if (Array.isArray(feature.properties.src_image_url)) {
        feature.properties.formatted.img = feature.properties.src_image_url[0];
      } else {
        feature.properties.formatted.img = feature.properties.src_image_url;
      }
    }

    feature.properties.formatted.source = null;
    if (lodashGet(feature, "properties.link")) {
      feature.properties.formatted.source = getUrlDomain(
        feature.properties.link
      );
    }
    if (feature.properties.id) {
      feature.properties.formatted.esId = feature.properties.id;
    }
    if (feature.properties.parent) {
      feature.properties.formatted.duplicate = feature.properties.parent;
    }
    if (feature.properties.translated) {
      feature.properties.formatted.translated = true;
    }
    if (
      feature.properties.title &&
      lodashGet(feature, "properties.source") !== "analysis"
    ) {
      feature.properties.formatted.title = feature.properties.title;
    }
    if (feature.properties.source === "twitter") {
      feature.properties.formatted.title = `@${feature.properties.author}`;
    }
    if (
      feature.properties.summary &&
      lodashGet(feature, "properties.source") !== "analysis"
    ) {
      feature.properties.formatted.summary = removeHtmlTags(
        feature.properties.summary
      );
    }
    if (
      lodashGet(feature, "properties.sanitizedBody") &&
      (lodashGet(feature, "properties.source") === "risk-rating-change" ||
        lodashGet(feature, "properties.source") === "evacuation")
    ) {
      feature.properties.formatted.summary = lodashGet(
        feature,
        "properties.sanitizedBody"
      );
    }
    if (feature.properties.lang) {
      feature.properties.formatted.language = feature.properties.lang;
      const language = getLanguage(feature.properties.lang);
      if (language?.name) {
        feature.properties.formatted.originalLanguage = language.name;
      }
    }
    if (lodashHas(feature.properties, "sentiment")) {
      feature.properties.formatted.sentiment = feature.properties.sentiment;
    }

    if (lodashHas(feature.properties, "emotions")) {
      feature.properties.formatted.emotions = feature.properties.emotions;
    }

    feature.properties.formatted.sourceVeracity = null;
    if (lodashGet(feature, "properties.veracity")) {
      feature.properties.formatted.sourceVeracity = feature.properties.veracity;
    }

    if (lodashGet(feature, "properties.topic_scores")) {
      let topicScores = feature.properties.topic_scores;
      if (typeof topicScores === "string" || topicScores instanceof String) {
        feature.properties = parseStringToObject(
          feature.properties,
          "topic_scores"
        );
      }
      topicScores = feature.properties.topic_scores;
      if (topicScores) {
        feature.properties.formatted.topic_scores =
          feature.properties.topic_scores;
        let scores = [];
        lodashForOwn(topicScores, (value, key) => {
          if (key && !isNaN(value)) {
            const TOPIC = findTopic(key, true, false);
            if (lodashGet(TOPIC, "scoreThreshold.news")) {
              scores.push(
                `<span class="application-heading">${key}</span>: ${(
                  value * 100
                ).toFixed(1)}% (${TOPIC.scoreThreshold.news * 100}%)`
              );
            }
          }
        });
        if (scores.length) {
          feature.properties.formatted.adminInfo += `${scores.join("<br/>")}`;
        }
      }
    }

    if (
      lodashGet(feature, "properties.location_metadata.method") &&
      feature.properties.location_metadata.method.toLowerCase() === "cliff"
    ) {
      let locParts = [];
      if (lodashGet(feature, "properties.location_metadata.city")) {
        locParts.push(feature.properties.location_metadata.city);
      }
      if (lodashGet(feature, "properties.location_metadata.state")) {
        locParts.push(feature.properties.location_metadata.state);
      }
      if (lodashGet(feature, "properties.location_metadata.country")) {
        locParts.push(feature.properties.location_metadata.country);
      }
      if (locParts.length) {
        feature.properties.formatted.locationName = locParts.join(", ");
      }
      locParts = null;
    }

    feature.properties.formatted.topic_scores_v2 = null;
    if (lodashGet(feature, "properties.topic_scores_v2")) {
      feature.properties.formatted.topic_scores_v2 =
        feature.properties.topic_scores_v2;
    }

    if (lodashHas(feature, "properties.severity")) {
      feature.properties.formatted.severity = feature.properties.severity;
    }

    if (lodashGet(feature, "properties.tags")) {
      const tags = feature.properties.tags.map((tag) => {
        return _propertyByLanguage(
          tag.name,
          feature.properties.formatted.language
        );
      });
      feature.properties.formatted.tags = tags.sort().join(", ");
    }

    // Verified Events
    if (
      feature.properties.source === "political" ||
      feature.properties.source === "maritime"
    ) {
      feature.properties.formatted.sourceVeracity = "Verified";
      await _formatVerified(feature);

      if (lodashGet(feature, "properties.references")) {
        feature.properties.formatted.references = getReferences(
          feature.properties.references
        );
      }

      // add published date to verified events (its completed field)
      feature.properties.formatted.published = feature.properties["@completed"]
        ? feature.properties["@completed"]
        : null;

      if (
        lodashGet(feature, "properties.countryCode") &&
        lodashGet(feature, "properties.countryName") &&
        lodashGet(feature, "properties.countryFlagUrl")
      ) {
        const countryResult = await getCountryByCode(
          feature.properties.countryCode
        );
        if (countryResult) {
          const cntry = JSON.parse(countryResult).data;
          if (cntry?.properties?.displayName) {
            feature.properties.countryName = cntry.properties.displayName;
          }
        }
        feature.properties.flagUrl = feature.properties.countryFlagUrl;
        feature.properties.formatted.countriesDisplayMetadata = await _getCountriesDisplayMetadata(
          feature
        );
      }

      //Verified Event water
      if (hasMaritimeAccess() && feature.properties.waterCode) {
        feature.properties.formatted.waters = [
          {
            code: feature.properties.waterCode,
            name:
              feature.properties.waterName ||
              getWaterByCode(feature.properties.waterCode)?.name?.text ||
              feature.properties.waterCode,
          },
        ];
      }
    }

    if (feature.properties.maritimeSource) {
      feature.properties.formatted.maritimeSources = feature.properties.maritimeSource.split(
        ";"
      );
    }

    // Analysis
    if (lodashGet(feature, "properties.source") === "analysis") {
      feature.properties.formatted.language = "en";
      if (lodashGet(feature, "properties.body")) {
        feature.properties.formatted.body = _propertyByLanguage(
          feature.properties.body,
          feature.properties.formatted.language
        );
        feature.properties.formatted.body = addTargetToLinks(
          feature.properties.formatted.body
        );
      }
      if (lodashGet(feature, "properties.sanitizedBody")) {
        feature.properties.formatted.sanitizedBody = _propertyByLanguage(
          feature.properties.sanitizedBody,
          feature.properties.formatted.language
        );
      }
      if (lodashGet(feature, "properties.sanitizedSummary")) {
        feature.properties.formatted.summary = _propertyByLanguage(
          feature.properties.sanitizedSummary,
          feature.properties.formatted.language
        );
      }
      if (!feature.properties.formatted.summary) {
        feature.properties.formatted.summary = _propertyByLanguage(
          feature.properties.sanitizedBody,
          feature.properties.formatted.language
        )?.substring(0, 300);
      }
      if (lodashGet(feature, "properties.publishedDate")) {
        feature.properties.formatted.published = getFormatted(
          feature.properties.publishedDate
        );
      }
      if (lodashGet(feature, "properties.updated")) {
        feature.properties.formatted.updated = getFormatted(
          feature.properties.updated
        );
      }
      if (lodashGet(feature, "properties.countries")) {
        feature.properties.formatted.countries = feature.properties.countries.map(
          (country) => {
            return country.name;
          }
        );
        feature.properties.formatted.countriesWithFlag = buildCountriesDisplay(
          feature.properties.countries,
          feature.properties.formatted.language
        );
        feature.properties.formatted.countriesDisplayMetadata = await _getCountriesDisplayMetadata(
          feature
        );
      }
      if (
        hasMaritimeAccess() &&
        feature.properties?.waters &&
        feature.properties.waters.length
      ) {
        feature.properties.formatted.waters = feature.properties.waters.map(
          (water) => {
            const formattedWater = { ...water };
            if (!water.name && water.code) {
              formattedWater.name = getWaterByCode(water.code)?.name[0]?.text;
            }
            return formattedWater;
          }
        );
      }

      if (lodashGet(feature, "properties.risks")) {
        const risks = feature.properties.risks.map((risk) => {
          if (risk && risk.name) {
            return upperCaseFirstLetter(risk.name);
          }
          return null;
        });
        feature.properties.formatted.risks = risks.join(", ");
      }
      if (lodashGet(feature, "properties.attachments")) {
        feature.properties.formatted.attachments = Object.entries(
          feature.properties.attachments
        ).map((entry) => {
          const [lang, attachmentArr] = entry;
          const attachment = attachmentArr[0];
          if (attachment && attachment.url) {
            const type = attachment.url.substring(attachment.url.length - 3);
            let size = attachment.size;
            let sizeFormatted = null;
            if (size) {
              sizeFormatted = formatFileSize(size, 0);
            }
            return {
              url: attachment.url,
              type: type.toLowerCase(),
              author: attachment.author || null,
              title: attachment.title || null,
              size: attachment.size || null,
              sizeFormatted: sizeFormatted,
              language: lang,
            };
          }
        });
      }
      if (feature.properties.title) {
        feature.properties.formatted.title = _propertyByLanguage(
          feature.properties.title,
          feature.properties.formatted.language
        );
      }

      // Internationa SOS specific data
      if (feature.properties.isos) {
        feature.properties.formatted.travelSource = "International SOS";
        if (feature.properties.isos.level) {
          feature.properties.formatted.travelLevel =
            feature.properties.isos.level;
        }
        if (feature.properties.isos.categories) {
          feature.properties.formatted.travelCategories = feature.properties.isos.categories.join(
            ", "
          );
        }
      }
    }

    // Risk Rating Change & Evacuation & Election
    if (
      feature.properties.source === "risk-rating-change" ||
      feature.properties.source === "evacuation" ||
      feature.properties.source === "elections"
    ) {
      if (lodashGet(feature, "properties.sanitizedBody")) {
        feature.properties.formatted.summary = _propertyByLanguage(
          feature.properties.sanitizedBody,
          feature.properties.formatted.language || "en"
        );
      }
      if (lodashGet(feature, "properties.title")) {
        feature.properties.formatted.title = _propertyByLanguage(
          feature.properties.title,
          feature.properties.formatted.language || "en"
        );
      }
      if (
        lodashGet(feature, "properties.flagUrl") &&
        lodashGet(feature, "properties.countryName") &&
        lodashGet(feature, "properties.countryCode")
      ) {
        feature.properties.formatted.countries = lodashGet(
          feature,
          "properties.countryName"
        );
        feature.properties.formatted.countriesWithFlag = buildCountriesDisplay(
          [
            {
              flagUrl: lodashGet(feature, "properties.flagUrl"),
              code: lodashGet(feature, "properties.countryCode"),
              name: { en: lodashGet(feature, "properties.countryName") },
            },
          ],
          lodashGet(feature, "properties.formatted.language")
        );
        feature.properties.formatted.countriesDisplayMetadata = await _getCountriesDisplayMetadata(
          feature
        );
      }
      let countriesDisplayMetadata = await _getCountriesDisplayMetadata(
        feature
      );
      if (countriesDisplayMetadata) {
        feature.properties.countriesDisplayMetadata = countriesDisplayMetadata;
      }
    }
  }

  // remove extra properties
  delete feature.properties.topic_scores_v2;
  delete feature.properties.topic_scores;
  delete feature.properties.common_src;
  delete feature.properties.src;
  delete feature.properties.location_metadata;
};

const _formatVerified = async (feature) => {
  let mapping = null;
  const remappedFields = {};
  const additionalFields = {
    topic_scores_v2: "topic_scores_v2",
  };

  if (!lodashGet(feature, "properties.summary")) {
    feature.properties.formatted.summary = feature.properties.title;
  }

  if (feature.properties.source === "maritime") {
    mapping = MARITIME_MAPPING;
  } else if (feature.properties.source === "political") {
    mapping = SECURITY_MAPPING;
  } else {
    return null;
  }

  for (const [key, value] of Object.entries(additionalFields)) {
    if (feature.properties[key]) {
      feature.properties.formatted[value] = feature.properties[key];
    }
  }

  for (const [field] of Object.entries(mapping)) {
    let camelCaseField = camelCase(field);
    if (remappedFields.hasOwnProperty(field) && feature.properties.field) {
      feature.properties.formatted[remappedFields[field]] =
        feature.properties.field;
    } else if (feature.properties[camelCaseField]) {
      feature.properties.formatted[camelCaseField] =
        feature.properties[camelCaseField];
    }
  }

  if (
    lodashGet(feature, "properties.countryFlagUrl") &&
    lodashGet(feature, "properties.countryName") &&
    lodashGet(feature, "properties.countryCode")
  ) {
    feature.properties.formatted.countries = lodashGet(
      feature,
      "properties.countryName"
    );
    feature.properties.formatted.countriesWithFlag = buildCountriesDisplay(
      [
        {
          flagUrl: lodashGet(feature, "properties.countryFlagUrl") || "",
          code: lodashGet(feature, "properties.countryCode"),
          name: { en: lodashGet(feature, "properties.countryName") },
        },
      ],
      lodashGet(feature, "properties.formatted.language")
    );
    feature.properties.formatted.countriesDisplayMetadata = await _getCountriesDisplayMetadata(
      feature
    );
  }
};

function buildCountriesDisplay(countries, language = "en") {
  const countriesWithFlag = countries.map((country) => {
    const flagUrl = country.flagUrl;
    const code = country.code;
    let countryName = lodashGet(countries, "[0].name.en");
    if (!countryName) {
      countryName = lodashFilter(
        country.name,
        lodashMatchesProperty("languageCode", language)
      )[0].text;
    }
    let countryText = `<a data-country-code="${code}">`;
    if (country.code) {
      countryText += `<img src="${flagUrl}" height="14" class="ml-1" style="border:1px solid #d1d1d1; border-radius:7px; position:relative; top:3px;" data-country-code="${code}" />`;
    }
    countryText += ` <span class="mr-1" data-country-code="${code}">${countryName}</span></a>`;
    return countryText;
  });
  return countriesWithFlag.join(" ");
}

/**
 * Several fields in analysis are broken by language this supports the ability to dynamically display
 * different language but default to "en" when the translation isn't found.
 * @param _obj The array of objects to filter by language
 * @param _language The language code to chose, it will default to en
 * @returns {string}
 * @private
 */
const _propertyByLanguage = (_obj, _language) => {
  if (typeof _obj !== "object") {
    return "";
  } else if (typeof _obj === "object" && !_obj.hasOwnProperty(_language)) {
    return _obj["en"];
  } else {
    if (typeof _obj[_language] !== "string") {
      return toString(_obj[_language]);
    } else {
      return _obj[_language];
    }
  }
};

const _dataToCopy = async (items, colsToCopy, singleElement = false) => {
  let toCopy = "";
  const lineBreak = "\n";

  if (singleElement) {
    colsToCopy.forEach((key) => {
      if (Object.hasOwn(items, key)) {
        if (key === "maritimeSources") {
          items["maritimeSources"].forEach((k) => {
            toCopy += "SOURCES: " + k + lineBreak;
          });
        } else if (key === "summary" && items["sanitizedBody"]) {
          //skip summary when body exists
        } else if (key === "sanitizedBody") {
          //this check is separate to avoid the final else case
          if (items[key] != null) {
            toCopy += "BODY: " + items[key] + lineBreak;
          }
        } else if (key === "title" && items["source"] === "twitter.com") {
          toCopy += "USER: " + items[key] + lineBreak;
        } else if (key === "countries" && items.countriesDisplayMetadata) {
          const countryNames = items.countriesDisplayMetadata.map(
            (c) => c.name || ""
          );
          if (countryNames) {
            toCopy += "COUNTRIES: " + countryNames.join(",") + lineBreak;
          }
        } else if (
          key === "countries" &&
          !items.countriesDisplayMetadata &&
          items.countries
        ) {
          const countryNames = items.countries.map((c) => c.name || "");
          if (countryNames) {
            toCopy += "COUNTRIES: " + countryNames.join(",") + lineBreak;
          }
        } else if (key === "waters" && hasMaritimeAccess()) {
          const waterNames = items.waters.map((w) => w.name || "");
          if (waterNames) {
            toCopy += "WATERS: " + waterNames.join(",") + lineBreak;
          }
        } else {
          toCopy += key.toUpperCase() + ": " + items[key] + lineBreak;
        }
      }
    });
  } else {
    if (items[0].properties === undefined) {
      Object.keys(items).forEach((mainKey) => {
        toCopy += "--------------------------" + lineBreak;

        Object.keys(colsToCopy).forEach((colIndex) => {
          const colName = colsToCopy[colIndex];
          if (colName === "Categories") {
            toCopy +=
              "CATEGORIES: " +
              arrayToCommaSplitString(items[mainKey]["Categories"], true) +
              lineBreak;
          } else if (colName === "AttackTypes") {
            toCopy +=
              "ATTACK TYPE: " +
              arrayToCommaSplitString(items[mainKey]["AttackTypes"], true) +
              lineBreak;
          } else if (colName === "riskCategories") {
            toCopy +=
              "RISKS: " +
              arrayToCommaSplitString(items[mainKey]["riskCategories"], false) +
              lineBreak;
          } else if (colName === "SeverityScore") {
            toCopy += "SEVERITY : " + items[mainKey][colName] + lineBreak;
          } else {
            toCopy +=
              colName.toUpperCase() +
              ": " +
              items[mainKey][colName] +
              lineBreak;
          }
        });
      });
    } else {
      Object.keys(items).forEach((mainKey) => {
        const itemFormatted = items[mainKey].properties.formatted;

        //separator
        toCopy += "------------------------------" + lineBreak;

        colsToCopy.forEach((key) => {
          if (Object.hasOwn(itemFormatted, key)) {
            if (key === "maritimeSources") {
              itemFormatted["maritimeSources"].forEach((key) => {
                toCopy += "SOURCES: " + key + lineBreak;
              });
            } else if (
              itemFormatted["source"] === "twitter.com" &&
              key === "title"
            ) {
              toCopy += "USER: " + itemFormatted[key] + lineBreak;
            } else if (key === "topic_scores_v2") {
              if (topicsDisplayList(items[mainKey])) {
                toCopy +=
                  "EVENTS: " + topicsDisplayList(items[mainKey]) + lineBreak;
              } else if (
                itemFormatted[key] &&
                (itemFormatted.type === "verifiedEvent" ||
                  itemFormatted.eventType === "verified")
              ) {
                toCopy +=
                  "CATEGORIES: " +
                  arrayToCommaSplitString(itemFormatted[key], true) +
                  lineBreak;
              }
            } else if (
              key === "countries" &&
              itemFormatted.countriesDisplayMetadata
            ) {
              const countryNames = itemFormatted.countriesDisplayMetadata.map(
                (c) => c.name || ""
              );
              if (countryNames) {
                toCopy += "COUNTRIES: " + countryNames.join(",") + lineBreak;
              }
            } else if (key === "waters" && hasMaritimeAccess()) {
              const waterNames = itemFormatted.waters.map((w) => w.name || "");
              if (waterNames) {
                toCopy += "WATERS: " + waterNames.join(",") + lineBreak;
              }
            } else {
              toCopy +=
                key.toUpperCase() + ": " + itemFormatted[key] + lineBreak;
            }
          }
        });
      });
    }
  }

  const clipboardItem = new ClipboardItem({
    "text/plain": new Blob([toCopy], { type: "text/plain" }),
    "text/html": new Blob([toCopy], { type: "text/html" }),
  });

  await navigator.clipboard.write([clipboardItem]);
};

const topicsDisplayList = (item) => {
  let topicScores = lodashGet(item, "properties.formatted.topic_scores");
  if (topicScores) {
    let thresholdToUse = getThresholdTypeFunction(item);
    return getTopicsStringFromTopicsScores(topicScores, thresholdToUse);
  }
  return null;
};

const getThresholdTypeFunction = (doc) => {
  let thresholdType = "cluster";
  if (
    lodashGet(doc, "properties.formatted.source") === "twitter.com" ||
    lodashGet(doc, "properties.formatted.source") === "twitter"
  ) {
    thresholdType = "twitter";
  }
  return thresholdType;
};

const arrayToCommaSplitString = (array, hasName) => {
  return array
    .map((key) => {
      if (hasName) {
        if (key.Name === undefined) {
          return key.name;
        } else {
          return key.Name;
        }
      } else {
        return key;
      }
    })
    .join(",");
};

const _getCountriesDisplayMetadata = async (feature) => {
  if (lodashGet(feature, "properties.countries")) {
    return lodashGet(feature, "properties.countries").map((country) => {
      return {
        code: country.code,
        name: _getCountryNameEnglish(country),
        flagUrl: country.flagUrl,
      };
    });
  }
  if (
    lodashGet(feature, "properties.flagUrl") &&
    lodashGet(feature, "properties.countryName") &&
    lodashGet(feature, "properties.countryCode")
  ) {
    return [
      {
        flagUrl: lodashGet(feature, "properties.flagUrl"),
        code: lodashGet(feature, "properties.countryCode"),
        name: lodashGet(feature, "properties.countryName"),
      },
    ];
  }
};

const _getCountryNameEnglish = (country) => {
  if (Array.isArray(country.name) && country.name.length > 0) {
    for (let i = 0; i < country.name.length; i++) {
      const cur = country.name[i];
      if (cur.languageCode === "en") {
        return cur.text;
      }
    }
  }
};

const removeUnauthorizedParams = async (params) => {
  //Make sure maritime is not sent as a source without access
  const allSources = params.sources?.split(",");
  if (!hasMaritimeAccess() && allSources?.includes("maritime")) {
    params.sources = allSources.filter((s) => s !== "maritime").join(",");
  }

  if (!hasMaritimeAccess() && params?.riskCategories?.length) {
    const allRiskCategories = params.riskCategories.split(",");
    const maritimeRiskCategoryIds = (await getMaritimeRiskCategories()).map(
      (c) => c.id
    );
    params.riskCategories =
      allRiskCategories
        .filter((id) => !maritimeRiskCategoryIds.includes(id))
        ?.join(",") || "";
  }
};
